/**
 * QA environments' hostnames: QA (hostqa.bidyourstay.com, qa.bidyourstay.com) and numbered
 * ones like QA2 (hostqa2.bidyourstay.com, qa2.bidyourstay.com). A plain `includes('hostqa.')`
 * check misses hostqa2, which sent QA2 payment/preview links to production.
 */
const QA_HOST_PATTERN = /(^|\.)(host)?qa(\d*)\./;

export function isQaHost(hostname) {
  return QA_HOST_PATTERN.test((hostname || '').toLowerCase());
}

/**
 * Guest-site / backend origin for a QA host: hostqa2.* → https://qa2.bidyourstay.com,
 * any other QA host (hostqa.*, localhost) → https://qa.bidyourstay.com.
 */
export function getQaOrigin(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  const match = (hostname || '').toLowerCase().match(QA_HOST_PATTERN);
  return `https://qa${match ? match[3] : ''}.bidyourstay.com`;
}

/**
 * API base URI for backend calls.
 * - Development: always qa.bidyourstay.com
 * - Production on a QA host (hostqa.bidyourstay.com, localhost): use qa.bidyourstay.com (never hostqa);
 *   hostqa2.bidyourstay.com uses qa2.bidyourstay.com
 * - Production on main host: relative /bys/api (same origin)
 * Resolved at runtime so each QA host uses its own environment's backend.
 */
export function getApiBaseUri() {
  // Allow overriding via env (recommended for local backend)
  if (process.env.REACT_APP_API_BASE_URI) {
    return process.env.REACT_APP_API_BASE_URI;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'https://qa.bidyourstay.com/bys/api';
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isQA =
      hostname.includes('qa') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1';
    if (isQA) {
      return `${getQaOrigin(hostname)}/bys/api`;
    }
  }
  return '/bys/api';
}

// Resolve at runtime (toString/valueOf when used in URLs) so hostqa gets qa.bidyourstay.com, not hostqa
export const API_BASE_URI = {
  toString() {
    return getApiBaseUri();
  },
  valueOf() {
    return getApiBaseUri();
  },
};

/**
 * Get the base URL for referral links based on the current environment
 * @returns {string} The base URL for the referral links
 */
export const getReferralBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isQA =
      hostname.includes('qa') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1';

    if (isQA) {
      return getQaOrigin(hostname);
    }

    return 'https://unpaqd.com';
  }

  // Default to QA for SSR
  return 'https://qa.bidyourstay.com';
};

/**
 * Generate a referral URL for an affiliate to share.
 *
 * Uses the backend `/r/{aid}` redirect endpoint, which sets the `aff_ref`
 * cookie server-side (HttpOnly, SameSite=Lax, 30-day) and 302s to the
 * destination — matches the contract in
 * docs/superpowers/specs/2026-07-27-affiliate-dashboard-design.md.
 *
 * @param {string} aid - The affiliate ID (AID)
 * @returns {string} The full referral URL
 */
export const generateAffiliateReferralUrl = (aid) => {
  const baseUrl = getReferralBaseUrl();
  return `${baseUrl}/r/${encodeURIComponent(aid)}`;
};
